import React, { useState, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

interface FormData {
  first_name: string;
  last_name: string;
  title: string;
  company: string;
  country_region: string;
  phone: string;
  email: string;
  product_type: 'perplexity_meet' | 'reports_detective';
  plan_amount: number;
  user_seats: number;
  trial_days: number;
}

interface Plan {
  name: string;
  price: number;
  seats: number;
  description: string;
}

const reportsDetectivePlans: Plan[] = [
  { name: 'Start Up', price: 4, seats: 3, description: '3-day free trial, Up to 3 team members' },
  { name: 'Small Teams', price: 10, seats: 10, description: '3-day free trial, Up to 10 team members' },
  { name: 'Small Business', price: 25, seats: 25, description: '3-day free trial, Up to 25 team members' }
];

const perplexityMeetPlans: Plan[] = [
  { name: "Start Up's", price: 4, seats: 2, description: '7-day free trial, 2 users included' },
  { name: 'K-12 & Instructors', price: 10, seats: 30, description: '7-day free trial, Up to 30 users' },
  { name: 'Small Business', price: 25, seats: 30, description: '7-day free trial, Up to 30 users' }
];

export default function RequestAccess() {
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    title: '',
    company: '',
    country_region: '',
    phone: '',
    email: '',
    product_type: 'perplexity_meet',
    plan_amount: perplexityMeetPlans[0].price,
    user_seats: perplexityMeetPlans[0].seats,
    trial_days: 7
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [selectedPlan, setSelectedPlan] = useState<string>('0');

  useEffect(() => {
    const plans = formData.product_type === 'perplexity_meet' ? perplexityMeetPlans : reportsDetectivePlans;
    const plan = plans[parseInt(selectedPlan)];
    const trialDays = formData.product_type === 'perplexity_meet' ? 7 : 3;
    
    setFormData(prev => ({
      ...prev,
      plan_amount: plan.price,
      user_seats: plan.seats,
      trial_days: trialDays
    }));
  }, [selectedPlan, formData.product_type]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'product_type') {
      setSelectedPlan('0'); // Reset plan selection when product type changes
      const trialDays = value === 'perplexity_meet' ? 7 : 3;
      const plans = value === 'perplexity_meet' ? perplexityMeetPlans : reportsDetectivePlans;
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        trial_days: trialDays,
        plan_amount: plans[0].price,
        user_seats: plans[0].seats
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase
        .from('access_requests')
        .insert([formData]);

      if (error) throw error;
      
      setSubmitStatus('success');
      setFormData({
        first_name: '',
        last_name: '',
        title: '',
        company: '',
        country_region: '',
        phone: '',
        email: '',
        product_type: 'perplexity_meet',
        plan_amount: perplexityMeetPlans[0].price,
        user_seats: perplexityMeetPlans[0].seats,
        trial_days: 7
      });
      setSelectedPlan('0');
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentPlans = formData.product_type === 'perplexity_meet' ? perplexityMeetPlans : reportsDetectivePlans;

  return (
    <div className="min-h-screen bg-background text-text relative">
      <div className="bubble"></div>
      <div className="bubble"></div>
      <div className="bubble"></div>
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-32">
        <h1 className="text-4xl font-bold mb-8 heading-gradient text-center">Request Product Access</h1>
        
        {submitStatus === 'success' ? (
          <div className="bg-primary/10 border border-primary rounded-lg p-6 text-center">
            <h3 className="text-2xl font-bold text-primary mb-2">Thank You!</h3>
            <p className="text-gray-300">
              Your request has been received. We'll review it and get back to you shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-300">
                  First Name *
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-300">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-300">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-300">
                  Company *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="country_region" className="block text-sm font-medium text-gray-300">
                  Country/Region *
                </label>
                <input
                  type="text"
                  id="country_region"
                  name="country_region"
                  required
                  value={formData.country_region}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="product_type" className="block text-sm font-medium text-gray-300">
                  Product *
                </label>
                <select
                  id="product_type"
                  name="product_type"
                  required
                  value={formData.product_type}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border border-gray-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="perplexity_meet">Perplexity Meet (7-day free trial)</option>
                  <option value="reports_detective">Reports Detective (3-day free trial)</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Plan *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {currentPlans.map((plan, index) => (
                    <div
                      key={plan.name}
                      className={`cursor-pointer rounded-lg p-4 transition-colors ${
                        selectedPlan === index.toString()
                          ? 'bg-primary bg-opacity-20 border-2 border-primary'
                          : 'bg-gray-800 border-2 border-transparent hover:border-primary/50'
                      }`}
                      onClick={() => setSelectedPlan(index.toString())}
                    >
                      <div className={`font-bold mb-1 ${
                        selectedPlan === index.toString() ? 'text-black' : 'text-white'
                      }`}>{plan.name}</div>
                      <div className={`text-2xl font-bold mb-2 ${
                        selectedPlan === index.toString() ? 'text-black' : 'text-primary'
                      }`}>
                        ${plan.price}
                        <span className={`text-sm ${
                          selectedPlan === index.toString() ? 'text-black/60' : 'text-gray-400'
                        }`}>/month</span>
                      </div>
                      <div className={`text-sm ${
                        selectedPlan === index.toString() ? 'text-black/80' : 'text-gray-300'
                      }`}>{plan.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hidden inputs for plan amount and user seats - auto-filled based on selection */}
              <input type="hidden" name="plan_amount" value={formData.plan_amount} />
              <input type="hidden" name="user_seats" value={formData.user_seats} />
            </div>

            <div className="flex items-center justify-end space-x-4">
              {submitStatus === 'error' && (
                <span className="text-red-400">Failed to send request. Please try again.</span>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="gradient-border bg-black text-white px-6 py-3 rounded-lg inline-flex items-center hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2" />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      <Footer />
    </div>
  );
}